import { useEffect, useMemo, useState } from 'react';
import { trendingApi, featuredArticlesApi, pageContentApi } from './api';

// Bucket for the trending rank call -- must match the sectionType PageVisitTracker/
// TrendingSection already use for /articles/* pages (see ArticlesPage.jsx), so
// popularity here reflects the same recorded PageVisitTracker visits.
const SECTION_TYPE = 'article';

const STOPWORDS = new Set([
  'the', 'and', 'for', 'with', 'that', 'this', 'from', 'into', 'onto', 'used', 'uses',
  'using', 'works', 'work', 'how', 'does', 'what', 'where', 'which', 'when', 'their',
  'these', 'those', 'than', 'then', 'have', 'has', 'are', 'was', 'were', 'been', 'being',
  'also', 'such', 'each', 'other', 'more', 'most', 'some', 'while', 'through', 'between',
  'across', 'over', 'under', 'about', 'both', 'can', 'its', 'via',
]);

// Articles carry no free-form tags field (only `category`/`subcategory`), so the closest
// available proxy for "overlapping tags/keywords" is the significant words shared between
// two articles' title + description text -- same approach as lib/relatedTopics.js.
const keywordsOf = (article) => {
  const text = `${article.title} ${article.description || ''}`.toLowerCase();
  const words = text.match(/[a-z][a-z-]{3,}/g) || [];
  return new Set(words.filter((w) => !STOPWORDS.has(w)));
};

const keywordOverlapScore = (a, b) => {
  if (a.size === 0 || b.size === 0) return 0;
  let shared = 0;
  for (const w of a) if (b.has(w)) shared += 1;
  return shared / Math.min(a.size, b.size);
};

const bareSlugFromPcSlug = (slug) => (slug || '').replace(/^article-/, '');
const bareSlugFromPath = (path) => (path || '').replace(/^\/articles\/(reference\/)?/, '').replace(/^article-/, '');

// The "Engineering Reference Series" catalog lives in two DB-backed sources for the same
// underlying page_contents docs: `featured_articles` (the promoted cards, linking to a
// dedicated /articles/<slug> route) and the generic published-articles list (linking to
// /articles/reference/<slug>). Merges them into one catalog keyed by bare slug, same dedup
// identity ArticlesPage.jsx's own trending row already uses -- reference entries supply the
// full category/subcategory metadata (featured_articles cards carry no subcategory), and a
// matching featured entry's `path` overrides the route since it's the promoted/canonical URL.
const buildCatalog = (featuredArticles, referenceArticles) => {
  const bySlug = new Map();
  referenceArticles.forEach((a) => {
    const bareSlug = bareSlugFromPcSlug(a.slug);
    bySlug.set(bareSlug, {
      bareSlug,
      to: `/articles/reference/${a.slug}`,
      title: a.title,
      description: a.description,
      category: a.category,
      subcategory: a.subcategory,
      updatedAt: a.updated_at,
    });
  });
  featuredArticles.forEach((a) => {
    if (!a.path) return;
    const bareSlug = bareSlugFromPath(a.path);
    const existing = bySlug.get(bareSlug);
    if (existing) {
      existing.to = a.path;
    } else {
      bySlug.set(bareSlug, {
        bareSlug,
        to: a.path,
        title: a.title,
        description: a.description,
        category: a.category,
        subcategory: null,
        updatedAt: a.updated_at,
      });
    }
  });
  return Array.from(bySlug.values());
};

// Fetches the full Engineering Reference Series catalog once (featured + reference-list
// articles merged), the same two sources ArticlesPage.jsx already fetches independently.
export const useArticleCatalog = () => {
  const [featured, setFeatured] = useState([]);
  const [reference, setReference] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([featuredArticlesApi.getFeaturedArticles(), pageContentApi.getPublicArticleList()])
      .then(([f, r]) => {
        if (cancelled) return;
        setFeatured(f);
        setReference(r);
      })
      .catch((err) => {
        console.error('Error fetching article catalog:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const catalog = useMemo(() => buildCatalog(featured, reference), [featured, reference]);
  return { catalog, loading };
};

// Scores `candidates` against `current` in priority order: (1) same category/subcategory,
// (2) keyword overlap -- same two-tier scheme as lib/relatedTopics.js.
export const scoreRelatedCandidates = (current, candidates) => {
  const currentWords = keywordsOf(current);
  return candidates.map((article) => {
    let tier = 0;
    if (current.category && article.category === current.category) {
      tier = current.subcategory && article.subcategory === current.subcategory ? 2 : 1;
    }
    return { article, tier, keywordScore: keywordOverlapScore(currentWords, keywordsOf(article)) };
  });
};

// Full ranking of `catalog` against `current` (the page_contents doc being viewed), best
// match first (current article itself excluded). `popularityRank` is a Map of bareSlug ->
// rank position from the trending endpoint (lower = more popular, missing = never visited
// => sorts last); ties beyond that fall back to most-recently-updated.
export const rankRelatedArticles = (current, catalog, popularityRank = new Map()) => {
  const currentBareSlug = bareSlugFromPcSlug(current.slug);
  const scored = scoreRelatedCandidates(
    current,
    catalog.filter((a) => a.bareSlug !== currentBareSlug)
  );
  return scored
    .sort((a, b) => {
      if (b.tier !== a.tier) return b.tier - a.tier;
      if (b.keywordScore !== a.keywordScore) return b.keywordScore - a.keywordScore;
      const rankA = popularityRank.has(a.article.bareSlug) ? popularityRank.get(a.article.bareSlug) : Infinity;
      const rankB = popularityRank.has(b.article.bareSlug) ? popularityRank.get(b.article.bareSlug) : Infinity;
      if (rankA !== rankB) return rankA - rankB;
      return new Date(b.article.updatedAt || 0) - new Date(a.article.updatedAt || 0);
    })
    .map((s) => s.article);
};

// Up to `count` articles most related to `current` (the full page_contents doc already
// fetched by DataDrivenArticlePage), drawn from the merged catalog -- same "filter/rank the
// candidate list client-side" pattern as lib/relatedTopics.js, no new backend endpoint.
// Returns [] until the catalog and `current` have both loaded.
export const useRelatedArticles = (current, count = 3) => {
  const { catalog, loading } = useArticleCatalog();
  const [popularityRank, setPopularityRank] = useState(new Map());
  const currentBareSlug = current ? bareSlugFromPcSlug(current.slug) : null;

  useEffect(() => {
    if (!current || catalog.length === 0) return undefined;
    let cancelled = false;
    const candidates = catalog.filter((a) => a.bareSlug !== currentBareSlug);
    const items = candidates.map((a) => ({ item_id: a.to, updated_at: a.updatedAt || null }));
    const bareSlugByTo = new Map(candidates.map((a) => [a.to, a.bareSlug]));
    trendingApi
      .rank(SECTION_TYPE, items)
      .then((ids) => {
        if (cancelled) return;
        const rankMap = new Map();
        ids.forEach((id, i) => {
          const bareSlug = bareSlugByTo.get(id);
          if (bareSlug) rankMap.set(bareSlug, i);
        });
        setPopularityRank(rankMap);
      })
      .catch(() => {
        if (!cancelled) setPopularityRank(new Map());
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBareSlug, catalog.length]);

  if (!current || loading) return [];
  return rankRelatedArticles(current, catalog, popularityRank).slice(0, count);
};
